// src/components/posts/VideoPostCard/VideoPostCard.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import cls from "./VideoPostCard.module.scss";

import api from "@/utils/axios";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";
import { useAuth } from "@/app/auth-context-state";
import { getCitizenshipThemeKey } from "@/utils/citizenshipTheme";
import { formatCommentTimestamp } from "@/utils/commentTimestamp";

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;
const isMongoId = (v) => /^[a-f\d]{24}$/i.test(safeStr(v).trim());

const fmtTime = (d) => formatCommentTimestamp(d).label;

const readToken = () => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("cm_at") || "";
};

const READY_SET = new Set(["ready", "published"]);

const REACTIONS = [
  { key: "heart", emoji: "❤️", label: "love" },
  { key: "haha", emoji: "😂", label: "haha" },
  { key: "wow", emoji: "😮", label: "wow" },
  { key: "sexy", emoji: "🔥", label: "sexy" },
  { key: "sad", emoji: "🤌", label: "wtf" },
];

const endpoints = (postId) => ({
  single: `/v1/posts/${postId}`,
  recordView: `/v1/posts/${postId}/view`,
  setReaction: `/v1/posts/${postId}/reaction`,
  removeReaction: `/v1/posts/${postId}/reaction`,

  addComment: `/v1/posts/${postId}/comments`,
  thread: `/v1/posts/${postId}/comments`,

  editComment: (commentId) => `/v1/posts/comments/${commentId}`,
  deleteComment: (commentId) => `/v1/posts/comments/${commentId}`,
  addReply: (commentId) => `/v1/posts/${postId}/comments/${commentId}/replies`,
});

const pickVideoMeta = (p) => {
  const rawVideoId =
    safeStr(p?.video?.videoId) ||
    safeStr(p?.preview?.videoId) ||
    (typeof p?.videoId === "string"
      ? safeStr(p.videoId)
      : safeStr(p?.videoId?._id)) ||
    "";

  const posterUrl =
    safeStr(p?.video?.posterUrl) ||
    safeStr(p?.video?.poster) ||
    safeStr(p?.preview?.posterUrl) ||
    safeStr(p?.preview?.poster) ||
    "";

  const videoUrl =
    safeStr(p?.video?.url) ||
    safeStr(p?.video?.videoUrl) ||
    safeStr(p?.preview?.url) ||
    safeStr(p?.preview?.videoUrl) ||
    "";

  const videoId = isMongoId(rawVideoId) ? rawVideoId.trim() : "";
  return { videoId, posterUrl: posterUrl.trim(), videoUrl: videoUrl.trim() };
};

const flattenThreadRows = (rows) => {
  const out = [];
  if (!Array.isArray(rows)) return out;

  for (const r of rows) {
    if (r?.comment) out.push({ ...r.comment, _kind: "comment" });
    if (Array.isArray(r?.replies)) {
      for (const rep of r.replies) out.push({ ...rep, _kind: "reply" });
    }
  }
  return out;
};

const useIsDesktop = (bp = 900) => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= bp;
  });

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= bp);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);

  return isDesktop;
};

// ==============================
// ✅ STABLE COMMENT COMPONENTS
// ==============================

const CommentAvatar = React.memo(function CommentAvatar({
  c,
  pickAuthor,
  onStop,
}) {
  const a = pickAuthor(c);
  const avatarTheme = getCitizenshipThemeKey(
    a?.citizenshipType || c?.citizenshipType || c?.author?.citizenshipType,
  );

  const avatar = (
    <div
      className={`${cls.cAvatarGlow} ${cls[`avatarTheme--${avatarTheme}`] || ""}`}
    >
      {a.av ? (
        <img className={cls.cAvatarImg} src={a.av} alt={a.name} />
      ) : (
        <div className={cls.cAvatarFallback}>{a.name?.[0] || "M"}</div>
      )}
    </div>
  );

  if (!a.href) return <div className={cls.cAvatarWrap}>{avatar}</div>;

  return (
    <Link
      to={a.href}
      className={cls.cAvatarWrap}
      onClick={onStop}
      aria-label={a.name}
      title={a.name}
    >
      {avatar}
    </Link>
  );
});

const CommentItem = React.memo(function CommentItem({
  c,
  inModal,

  pickAuthor,
  canDeleteComment,
  canEditComment,

  editId,
  editText,
  setEditText,
  editBusy,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,

  replyParentId,
  replyText,
  setReplyText,
  replyBusy,
  onStartReply,
  onCancelReply,
  onSubmitReply,

  confirmDelId,
  deleteBusy,
  onAskDelete,
  onConfirmDelete,
  onCancelDelete,

  onStop,

  // ✅ i18n
  t,
}) {
  const a = pickAuthor(c);
  const nameTheme = getCitizenshipThemeKey(
    a?.citizenshipType || c?.citizenshipType || c?.author?.citizenshipType,
  );
  const nameColorByTheme = {
    explorer: "#F5F7FA",
    nexus: "#00E5A8",
    lumora: "#6A00A8",
  };

  const isReply = c?._kind === "reply" || Number(c?.depth || 0) === 1;
  const isTop = !isReply;

  const showReplyBtn = inModal && isTop && !c?.isDeleted;
  const showDeleteBtn = inModal && !c?.isDeleted && canDeleteComment(c);
  const showEditBtn = inModal && canEditComment(c);
  const createdStamp = formatCommentTimestamp(c?.createdAt);

  const isEditing = isMongoId(editId) && String(editId) === String(c?._id);
  const isReplying =
    isMongoId(replyParentId) && String(replyParentId) === String(c?._id);

  const isConfirmingDelete =
    isMongoId(confirmDelId) && String(confirmDelId) === String(c?._id);

  return (
    <div className={`${cls.cRow} ${isReply ? cls.cRowReply : ""}`}>
      <CommentAvatar c={c} pickAuthor={pickAuthor} onStop={onStop} />

      <div className={cls.cRight}>
        <div className={cls.cTopLine}>
          {a.href ? (
            <Link
              to={a.href}
              className={cls.cNameLink}
              style={{ color: nameColorByTheme[nameTheme] }}
              onClick={onStop}
            >
              {a.name}
            </Link>
          ) : (
            <span
              className={cls.cNameLink}
              style={{ color: nameColorByTheme[nameTheme] }}
            >
              {a.name}
            </span>
          )}

          <span className={cls.cDot}>·</span>
          <time
            className={cls.cTime}
            dateTime={c?.createdAt || undefined}
            title={createdStamp.title}
          >
            {createdStamp.label}
          </time>
        </div>

        <div className={cls.cBubble}>
          {c?.isDeleted ? (
            <div className={cls.cDeleted}>
              {t("post.comments.removed", "Comment removed")}
            </div>
          ) : isEditing ? (
            <div
              className={cls.cEditor}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <textarea
                className={cls.cEditInput}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
              />

              <div className={cls.cEditorBtns}>
                <button
                  type="button"
                  className={cls.cBtnPrimary}
                  disabled={editBusy || !hasText(editText)}
                  onClick={onSubmitEdit}
                >
                  {t("post.actions.update", "Update")}
                </button>
                <button
                  type="button"
                  className={cls.cBtnGhost}
                  disabled={editBusy}
                  onClick={onCancelEdit}
                >
                  {t("post.actions.cancel", "Cancel")}
                </button>
              </div>
            </div>
          ) : (
            <div className={cls.cText}>{safeStr(c?.text)}</div>
          )}
        </div>

        {inModal ? (
          <div className={cls.cActions}>
            {showReplyBtn ? (
              <button
                type="button"
                className={cls.cActionBtn}
                onClick={() => onStartReply(c?._id)}
              >
                {t("post.actions.reply", "Reply")}
              </button>
            ) : null}

            {showEditBtn && !isEditing ? (
              <button
                type="button"
                className={cls.cActionBtn}
                onClick={() => onStartEdit(c)}
              >
                {t("post.actions.edit", "Edit")}
              </button>
            ) : null}

            {showDeleteBtn ? (
              <button
                type="button"
                className={`${cls.cActionBtn} ${cls.cActionDanger}`}
                onClick={() => onAskDelete(String(c?._id || ""))}
              >
                {t("post.actions.delete", "Delete")}
              </button>
            ) : null}
          </div>
        ) : null}

        {inModal && showDeleteBtn && isConfirmingDelete ? (
          <div
            className={cls.cConfirmRow}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <span className={cls.cConfirmText}>
              {t("post.confirm.areYouSure", "Are you sure?")}
            </span>

            <button
              type="button"
              className={cls.cConfirmYes}
              disabled={deleteBusy}
              onClick={() => onConfirmDelete(String(c?._id || ""))}
            >
              {t("post.confirm.yes", "Yes")}
            </button>

            <button
              type="button"
              className={cls.cConfirmNo}
              disabled={deleteBusy}
              onClick={onCancelDelete}
            >
              {t("post.confirm.no", "No")}
            </button>
          </div>
        ) : null}

        {inModal && isReplying ? (
          <div
            className={cls.cReplyBox}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              className={cls.cReplyInput}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t("post.inputs.replyPlaceholder", "Write a reply…")}
              enterKeyHint="send"
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmitReply();
              }}
            />

            <div className={cls.cReplyBtns}>
              <button
                type="button"
                className={cls.cBtnPrimary}
                disabled={replyBusy || !hasText(replyText)}
                onClick={onSubmitReply}
              >
                {t("post.actions.reply", "Reply")}
              </button>
              <button
                type="button"
                className={cls.cBtnGhost}
                disabled={replyBusy}
                onClick={onCancelReply}
              >
                {t("post.actions.cancel", "Cancel")}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

const VideoPostCard = ({ post }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const postId = safeStr(post?._id);

  const canViewPost =
    typeof post?.canViewPost === "boolean" ? post.canViewPost : true;

  const isDesktop = useIsDesktop(900);
  const [token, setToken] = useState(() => readToken());

  const postAuthorUserId = useMemo(() => {
    const raw =
      post?.authorId ||
      post?.author?._id ||
      post?.authorSnap?.userId ||
      post?.authorSnap?.authorId ||
      "";
    return isMongoId(raw) ? String(raw) : "";
  }, [post]);

  const [meUserId, setMeUserId] = useState("");
  const [meLoading, setMeLoading] = useState(false);

  useEffect(() => {
    const id = user?._id || user?.id || user?.userId || "";
    if (isMongoId(id)) setMeUserId(String(id));
  }, [user]);

  const loadMe = async () => {
    if (meLoading) return;
    if (hasText(meUserId)) return;

    setMeLoading(true);
    try {
      const { data } = await api.get("/v1/auth/me", { skipToast: true });
      const u = data?.user || data?.me || data || {};
      const id = u?._id || u?.id || u?.userId || "";
      if (isMongoId(id)) setMeUserId(String(id));
    } catch {
      // ignore
    } finally {
      setMeLoading(false);
    }
  };

  // modal
  const [open, setOpen] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const [fullErr, setFullErr] = useState("");
  const [fullPost, setFullPost] = useState(null);

  // reactions
  const [myReaction, setMyReaction] = useState(safeStr(post?.myReaction || ""));
  const [counts, setCounts] = useState(() => ({
    total: Number(post?.reactionCount || 0),
    heart: Number(post?.reactionCounts?.heart || 0),
    haha: Number(post?.reactionCounts?.haha || 0),
    wow: Number(post?.reactionCounts?.wow || 0),
    sexy: Number(post?.reactionCounts?.sexy || 0),
    sad: Number(post?.reactionCounts?.sad || 0),
  }));

  useEffect(() => {
    setMyReaction(safeStr(post?.myReaction || ""));
    setCounts({
      total: Number(post?.reactionCount || 0),
      heart: Number(post?.reactionCounts?.heart || 0),
      haha: Number(post?.reactionCounts?.haha || 0),
      wow: Number(post?.reactionCounts?.wow || 0),
      sexy: Number(post?.reactionCounts?.sexy || 0),
      sad: Number(post?.reactionCounts?.sad || 0),
    });
  }, [
    postId,
    post?.myReaction,
    post?.reactionCount,
    post?.reactionCounts?.heart,
    post?.reactionCounts?.haha,
    post?.reactionCounts?.wow,
    post?.reactionCounts?.sexy,
    post?.reactionCounts?.sad,
  ]);

  // comments preview (latest 2)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewComments, setPreviewComments] = useState([]);

  // full thread (modal)
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadRows, setThreadRows] = useState([]);
  const [threadMeta, setThreadMeta] = useState({ page: 1, hasMore: false });

  // comment input
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  // reply UI
  const [replyParentId, setReplyParentId] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);

  // edit UI
  const [editId, setEditId] = useState("");
  const [editText, setEditText] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  // ✅ inline delete confirm UI
  const [confirmDelId, setConfirmDelId] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const modalRef = useRef(null);

  // ✅ prevent body scroll while modal open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  // ✅ focus only when modal opens and never steal focus if typing
  useEffect(() => {
    if (!open) return;

    const tt = setTimeout(() => {
      try {
        const el = modalRef.current;
        if (!el) return;

        const ae = document.activeElement;
        const tag = (ae?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || ae?.isContentEditable)
          return;

        el.focus?.();
      } catch {
        // Intentionally ignored: focus restoration is a best-effort UI enhancement.
      }
    }, 0);

    return () => clearTimeout(tt);
  }, [open]);

  useEffect(() => {
    setToken(readToken());

    const onStorage = (e) => {
      if (e?.key === "cm_at") setToken(readToken());
    };
    window.addEventListener("storage", onStorage);

    let tries = 0;
    const tt = setInterval(() => {
      tries += 1;
      const next = readToken();
      setToken((prev) => (prev === next ? prev : next));
      if (tries >= 10) clearInterval(tt);
    }, 500);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(tt);
    };
  }, []);

  const authorName = safeStr(
    post?.authorSnap?.username ||
      post?.authorSnap?.displayName ||
      t("post.labels.member", "Member"),
  );

  const authorId = useMemo(() => {
    const id =
      post?.authorSnap?.memberId || post?.authorId || post?.author?._id || "";
    return isMongoId(id) ? String(id) : "";
  }, [post]);

  const authorHref = authorId ? `/lounge/member-profile/${authorId}` : "";

  const avatarUrl = safeStr(
    post?.authorSnap?.avatarThumbUrl ||
      post?.authorSnap?.avatarUrl ||
      post?.author?.avatarUrl ||
      "",
  );

  const postTheme = getCitizenshipThemeKey(
    post?.authorSnap?.citizenshipType ||
      post?.author?.citizenshipType ||
      post?.member?.citizenshipType ||
      post?.citizenshipType,
  );

  const isClubPost = safeStr(post?.visibility).toLowerCase() === "club";
  const clubData = useMemo(
    () => post?.clubSnap || post?.club || {},
    [post?.clubSnap, post?.club],
  );
  const clubId = useMemo(() => {
    const raw =
      clubData?.clubId ||
      clubData?._id ||
      post?.clubId ||
      post?.club?._id ||
      post?.clubSnap?.clubId ||
      "";
    return isMongoId(raw) ? String(raw) : "";
  }, [clubData, post]);

  const clubName = safeStr(
    clubData?.clubName || post?.clubName || t("post.labels.club", "Club"),
  );

  const clubAvatarUrl = safeStr(
    clubData?.clubAvatarThumb?.url ||
      clubData?.clubAvatar?.url ||
      clubData?.avatarThumb?.url ||
      clubData?.avatar?.url ||
      clubData?.clubAvatarThumbUrl ||
      clubData?.clubAvatarUrl ||
      clubData?.avatarThumbUrl ||
      clubData?.avatarUrl ||
      "",
  );

  const clubHref = clubId ? `/lounge/clubs/single/${clubId}/home` : "";
  const primaryName = isClubPost && hasText(clubName) ? clubName : authorName;
  const primaryAvatarUrl =
    isClubPost && hasText(clubAvatarUrl) ? clubAvatarUrl : avatarUrl;
  const primaryHref = isClubPost ? clubHref : authorHref;

  const when = useMemo(
    () => fmtTime(post?.publishAt || post?.createdAt || post?.updatedAt),
    [post],
  );

  const badges = useMemo(() => {
    return [safeStr(post?.visibility), safeStr(post?.postLanguage)].filter(
      (x) => hasText(x),
    );
  }, [post]);

  const isAuthor =
    isMongoId(meUserId) &&
    postAuthorUserId &&
    String(meUserId) === String(postAuthorUserId);
  const viewHref = postId ? `/lounge/single-post/${postId}` : "";

  const title = safeStr(post?.title);
  const desc = safeStr(post?.description);

  const { videoId, posterUrl, videoUrl } = useMemo(
    () => pickVideoMeta(post),
    [post],
  );

  const canProxyPlay = isMongoId(videoId) && hasText(token);
  const canDirectPlay = hasText(videoUrl);
  const looksLikeProcessing =
    isMongoId(videoId) && !canDirectPlay && !canProxyPlay;

  const [videoStatus, setVideoStatus] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    if (!isMongoId(videoId)) return;

    let alive = true;
    let timer = null;

    const tick = async () => {
      try {
        const { data } = await api.get(`/v1/videos/${videoId}`, {
          skipToast: true,
        });
        const doc = data?.video || data?.doc || data || {};
        const st = safeStr(doc?.status).toLowerCase();
        const msg = safeStr(doc?.message || doc?.statusMessage || "");

        if (!alive) return;
        if (st) setVideoStatus(st);
        if (msg) setStatusMsg(msg);

        if (READY_SET.has(st) || st === "failed") return;
      } catch {
        // ignore
      } finally {
        if (alive) {
          timer = setTimeout(tick, 5000);
        }
      }
    };

    if (looksLikeProcessing || !videoStatus || !READY_SET.has(videoStatus)) {
      timer = setTimeout(tick, 800);
    }

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [videoId, looksLikeProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldShowProcessing =
    looksLikeProcessing ||
    (!!videoStatus && !READY_SET.has(videoStatus) && videoStatus !== "failed");

  const stop = (e) => e.stopPropagation();

  const pickCommentAuthor = (c) => {
    const name = safeStr(
      c?.authorUsername || t("post.labels.member", "Member"),
    );
    const memberId = safeStr(c?.authorMemberId || "");
    const href = isMongoId(memberId)
      ? `/lounge/member-profile/${memberId}`
      : "";
    const av = safeStr(c?.authorAvatarThumbUrl || "");
    const citizenshipType = safeStr(c?.authorCitizenshipType || "");
    return { name, memberId, href, av, citizenshipType };
  };

  const canDeleteComment = (c) => {
    const commentOwner = safeStr(c?.authorId || "");
    if (!isMongoId(meUserId)) return false;
    if (String(meUserId) === String(commentOwner)) return true;
    if (postAuthorUserId && String(meUserId) === String(postAuthorUserId))
      return true;
    return false;
  };

  const canEditComment = (c) => {
    const commentOwner = safeStr(c?.authorId || "");
    if (!isMongoId(meUserId)) return false;
    if (String(meUserId) !== String(commentOwner)) return false;
    if (c?.isDeleted) return false;
    return true;
  };

  const loadPreviewComments = async () => {
    if (!postId) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.get(endpoints(postId).thread, {
        params: { page: 1, limit: 20 },
        skipToast: true,
      });
      const flat = flattenThreadRows(data?.rows || []);
      setPreviewComments(flat.slice(-2));
    } catch {
      setPreviewComments([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadPreviewComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const onReact = async (type) => {
    if (!postId) return;

    try {
      if (myReaction === type) {
        const { data } = await api.delete(endpoints(postId).removeReaction, {
          skipToast: true,
        });
        setMyReaction("");
        setCounts((prev) => data?.counts || prev);
        return;
      }

      const { data } = await api.post(
        endpoints(postId).setReaction,
        { type },
        { skipToast: true },
      );

      setMyReaction(data?.myReaction || type);
      setCounts((prev) => data?.counts || prev);
    } catch {
      // ignore
    }
  };

  const loadThread = async ({ page = 1, replace = false } = {}) => {
    if (!postId) return;
    setThreadLoading(true);

    try {
      const { data } = await api.get(endpoints(postId).thread, {
        params: { page, limit: 20 },
        skipToast: true,
      });

      const flat = flattenThreadRows(data?.rows || []);
      const meta = data?.meta || {};
      const hasMore = !!meta?.hasMore;

      setThreadMeta({ page, hasMore });
      setThreadRows((prev) => (replace ? flat : [...prev, ...flat]));
    } catch {
      // keep old
    } finally {
      setThreadLoading(false);
    }
  };

  const submitComment = async ({ inModal = false } = {}) => {
    if (!postId) return;
    const text = safeStr(commentText).trim();
    if (!text) return;

    setCommentBusy(true);
    try {
      await api.post(
        endpoints(postId).addComment,
        { text },
        { skipToast: true },
      );
      setCommentText("");
      await loadPreviewComments();
      if (inModal) await loadThread({ page: 1, replace: true });
    } catch {
      // ignore
    } finally {
      setCommentBusy(false);
    }
  };

  const startReply = (commentId) => {
    setConfirmDelId("");
    setEditId("");
    setEditText("");
    setReplyParentId(String(commentId || ""));
    setReplyText("");
  };

  const cancelReply = () => {
    setReplyParentId("");
    setReplyText("");
  };

  const submitReply = async () => {
    if (!postId) return;
    if (!isMongoId(replyParentId)) return;

    const text = safeStr(replyText).trim();
    if (!text) return;

    setReplyBusy(true);
    try {
      await api.post(
        endpoints(postId).addReply(replyParentId),
        { text },
        { skipToast: true },
      );
      setReplyText("");
      setReplyParentId("");
      await loadPreviewComments();
      await loadThread({ page: 1, replace: true });
    } catch {
      // ignore
    } finally {
      setReplyBusy(false);
    }
  };

  const startEdit = (c) => {
    setConfirmDelId("");
    setReplyParentId("");
    setReplyText("");
    setEditId(String(c?._id || ""));
    setEditText(safeStr(c?.text || ""));
  };

  const cancelEdit = () => {
    setEditId("");
    setEditText("");
  };

  const submitEdit = async () => {
    if (!postId) return;
    if (!isMongoId(editId)) return;

    const text = safeStr(editText).trim();
    if (!text) return;

    setEditBusy(true);
    try {
      await api.patch(
        endpoints(postId).editComment(editId),
        { text },
        { skipToast: true },
      );
      setEditId("");
      setEditText("");
      await loadPreviewComments();
      await loadThread({ page: 1, replace: true });
    } catch {
      // ignore
    } finally {
      setEditBusy(false);
    }
  };

  const askDelete = (commentId) => {
    if (!isMongoId(commentId)) return;
    setConfirmDelId((prev) =>
      String(prev) === String(commentId) ? "" : String(commentId),
    );
  };

  const cancelAskDelete = () => setConfirmDelId("");

  const confirmDelete = async (commentId) => {
    if (!postId) return;
    if (!isMongoId(commentId)) return;

    setDeleteBusy(true);
    try {
      await api.delete(endpoints(postId).deleteComment(commentId), {
        skipToast: true,
      });

      if (String(editId) === String(commentId)) cancelEdit();
      if (String(replyParentId) === String(commentId)) cancelReply();

      setConfirmDelId("");
      await loadPreviewComments();
      await loadThread({ page: 1, replace: true });
    } catch {
      // ignore
    } finally {
      setDeleteBusy(false);
    }
  };

  const openModal = async () => {
    if (!postId) return;
    if (!canViewPost) return;

    loadMe().catch(() => {});

    setOpen(true);
    setFullErr("");
    setFullLoading(true);

    try {
      api
        .post(endpoints(postId).recordView, {}, { skipToast: true })
        .catch(() => {});

      const { data } = await api.get(endpoints(postId).single, {
        skipToast: true,
      });
      setFullPost(data?.post || data || null);

      await loadThread({ page: 1, replace: true });
    } catch (e) {
      setFullErr(
        e?.response?.data?.message ||
          e?.message ||
          t("post.errors.failedToLoadPost", "Failed to load post"),
      );
    } finally {
      setFullLoading(false);
    }
  };

  const closeModal = useCallback(() => {
    setOpen(false);
    setFullPost(null);
    setThreadRows([]);
    setThreadMeta({ page: 1, hasMore: false });
    setCommentText("");
    setEditId("");
    setEditText("");
    setReplyParentId("");
    setReplyText("");
    setConfirmDelId("");
    setDeleteBusy(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeModal]);

  const AuthorWrap = ({ children }) => {
    if (!primaryHref) {
      return <div className={cls.authorLinkDisabled}>{children}</div>;
    }

    const handleClick = (e) => {
      if (e?.defaultPrevented) return;
      if (e?.target?.closest?.("a")) {
        stop(e);
        return;
      }
      stop(e);
      navigate(primaryHref);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick(e);
      }
    };
    return (
      <div
        className={cls.authorLink}
        role="link"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    );
  };

  const feedHeight = isDesktop ? 420 : 260;
  const modalHeight = isDesktop ? 540 : 300;

  const renderVideo = ({ big = false } = {}) => {
    if (shouldShowProcessing) {
      return (
        <div className={cls.processing}>
          {posterUrl ? (
            <img
              className={cls.processingPoster}
              src={posterUrl}
              alt={t("post.media.processingAlt", "processing")}
            />
          ) : (
            <div className={cls.processingPosterFallback} />
          )}

          <div className={cls.processingOverlay}>
            <div className={cls.spinner} aria-hidden="true" />
            <div className={cls.processingTitle}>
              {t("post.media.videoProcessingTitle", "Video is processing…")}
            </div>

            <div className={cls.processingSub}>
              {videoStatus
                ? t("post.media.statusLine", "Status: {{status}}", {
                    status: videoStatus,
                  })
                : t("post.media.preparingVideo", "Preparing video…")}
            </div>

            {hasText(statusMsg) ? (
              <div className={cls.processingSub}>{statusMsg}</div>
            ) : null}

            <div className={cls.processingHint}>
              {t(
                "post.media.processingHint",
                "It will appear automatically once it’s ready.",
              )}
            </div>
          </div>
        </div>
      );
    }

    if (canProxyPlay) {
      return (
        <div className={`${cls.videoPlayerWrap} ${big ? cls.videoBig : ""}`}>
          <VideoPlayer
            token={token}
            videoId={videoId}
            poster={posterUrl || undefined}
            mode="proxy"
            height={big ? modalHeight : feedHeight}
          />
        </div>
      );
    }

    if (canDirectPlay) {
      return (
        <div className={`${cls.videoPlayerWrap} ${big ? cls.videoBig : ""}`}>
          <video
            className={cls.nativeVideo}
            src={videoUrl}
            poster={posterUrl || undefined}
            controls
            preload="metadata"
            playsInline
            controlsList="nodownload"
          />
        </div>
      );
    }

    return (
      <div className={cls.noVideo}>
        {t("post.media.videoUnavailable", "Video unavailable")}
        {!hasText(token)
          ? ` ${t("post.media.missingToken", "(missing token)")}`
          : ""}
        {!isMongoId(videoId)
          ? ` ${t("post.media.missingVideoId", "(missing videoId)")}`
          : ""}
      </div>
    );
  };

  const cardDescHasMore = useMemo(
    () => safeStr(desc).trim().length > 120,
    [desc],
  );
  const previewVisibleComments = useMemo(() => {
    if (previewComments.length <= 1) return previewComments;
    if (isDesktop) return previewComments;
    return previewComments.slice(-1);
  }, [previewComments, isDesktop]);

  const modalUi =
    open && typeof document !== "undefined"
      ? createPortal(
          <div className={cls.modalOverlay} onMouseDown={closeModal}>
            <div
              className={cls.modal}
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => e.stopPropagation()}
              tabIndex={-1}
              ref={modalRef}
            >
              <button
                type="button"
                className={cls.closeBtn}
                onClick={closeModal}
                aria-label={t("post.actions.close", "Close")}
              >
                ✕
              </button>

              {fullLoading ? (
                <div className={cls.modalLoading}>
                  {t("post.loading.loadingPost", "Loading post…")}
                </div>
              ) : fullErr ? (
                <div className={cls.modalError}>{fullErr}</div>
              ) : (
                <>
                  <div className={cls.modalHeader}>
                    <AuthorWrap>
                      <div
                        className={`${cls.avatarGlow} ${cls[`avatarTheme--${postTheme}`] || ""}`}
                      >
                        {primaryAvatarUrl ? (
                          <img
                            className={cls.avatar}
                            src={primaryAvatarUrl}
                            alt={primaryName}
                          />
                        ) : (
                          <div className={cls.avatarFallback}>
                            {primaryName?.[0] || "M"}
                          </div>
                        )}
                      </div>
                      <div className={cls.authorMeta}>
                        {isClubPost ? (
                          <div className={cls.clubMetaStack}>
                            {clubHref ? (
                              <Link
                                to={clubHref}
                                className={cls.clubLink}
                                onClick={stop}
                              >
                                {clubName}
                              </Link>
                            ) : (
                              <span className={cls.clubName}>{clubName}</span>
                            )}
                            {hasText(authorName) ? (
                              authorHref ? (
                                <Link
                                  to={authorHref}
                                  className={cls.authorSubLink}
                                  onClick={stop}
                                >
                                  {authorName}
                                </Link>
                              ) : (
                                <span className={cls.authorSubName}>
                                  {authorName}
                                </span>
                              )
                            ) : null}
                            {hasText(when) ? (
                              <div className={cls.timeLine}>{when}</div>
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <div className={cls.authorName}>{authorName}</div>
                            <div className={cls.timeLine}>{when}</div>
                          </>
                        )}
                      </div>
                    </AuthorWrap>

                    <div className={cls.badges}>
                      {isAuthor && viewHref ? (
                        <Link
                          to={viewHref}
                          className={cls.viewBtn}
                          onClick={stop}
                        >
                          {t("post.actions.view", "View")}
                        </Link>
                      ) : (
                        badges.map((b) => (
                          <span key={b} className={cls.badge}>
                            {b}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {hasText(fullPost?.title || title) ? (
                    <h2 className={cls.modalTitle}>
                      {safeStr(fullPost?.title || title)}
                    </h2>
                  ) : null}

                  {hasText(fullPost?.description || desc) ? (
                    <p className={cls.modalDesc}>
                      {safeStr(fullPost?.description || desc)}
                    </p>
                  ) : null}

                  <div className={cls.modalMedia}>
                    {renderVideo({ big: true })}
                  </div>

                  <div className={cls.reactionsRow} onClick={stop}>
                    {REACTIONS.map((r) => {
                      const active = myReaction === r.key;
                      const n = Number(counts?.[r.key] || 0);
                      return (
                        <button
                          key={r.key}
                          type="button"
                          className={`${cls.reactBtn} ${
                            active ? cls.reactBtnActive : ""
                          }`}
                          onClick={() => onReact(r.key)}
                          aria-pressed={active}
                        >
                          <span className={cls.reactEmoji}>{r.emoji} </span>
                          <span className={cls.reactLabel}>{r.label} </span>
                          <span className={cls.reactCount}>{n}</span>
                        </button>
                      );
                    })}

                    <div className={cls.reactionMeta}>
                      <span className={cls.metaPill}>
                        ❤️ {Number(counts?.total || 0)}
                      </span>
                      <span className={cls.metaPill}>
                        💬{" "}
                        {Number(
                          fullPost?.commentCount || post?.commentCount || 0,
                        )}
                      </span>
                    </div>
                  </div>

                  <div className={cls.commentInputRow} onClick={stop}>
                    <textarea
                      className={cls.commentInput}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder={t(
                        "post.inputs.commentPlaceholder",
                        "Write a comment…",
                      )}
                      rows={1}
                      enterKeyHint="send"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          submitComment({ inModal: true });
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={cls.sendBtn}
                      disabled={commentBusy || !hasText(commentText)}
                      onClick={() => submitComment({ inModal: true })}
                    >
                      {t("post.actions.send", "Send")}
                    </button>
                  </div>

                  <div className={cls.thread} onClick={stop}>
                    <div className={cls.threadHeader}>
                      <div className={cls.threadTitle}>
                        {t("post.comments.title", "Comments")}
                      </div>
                      <div className={cls.threadMeta}>
                        {threadLoading
                          ? t("post.loading.loading", "Loading…")
                          : ""}
                      </div>
                    </div>

                    {threadRows.length === 0 && !threadLoading ? (
                      <div className={cls.smallHint}>
                        {t("post.comments.noneYet", "No comments yet.")}
                      </div>
                    ) : (
                      <div className={cls.threadList}>
                        {threadRows.map((c) => (
                          <CommentItem
                            key={safeStr(c?._id)}
                            c={c}
                            inModal
                            pickAuthor={pickCommentAuthor}
                            canDeleteComment={canDeleteComment}
                            canEditComment={canEditComment}
                            editId={editId}
                            editText={editText}
                            setEditText={setEditText}
                            editBusy={editBusy}
                            onStartEdit={startEdit}
                            onCancelEdit={cancelEdit}
                            onSubmitEdit={submitEdit}
                            replyParentId={replyParentId}
                            replyText={replyText}
                            setReplyText={setReplyText}
                            replyBusy={replyBusy}
                            onStartReply={startReply}
                            onCancelReply={cancelReply}
                            onSubmitReply={submitReply}
                            confirmDelId={confirmDelId}
                            deleteBusy={deleteBusy}
                            onAskDelete={askDelete}
                            onConfirmDelete={confirmDelete}
                            onCancelDelete={cancelAskDelete}
                            onStop={stop}
                            t={t}
                          />
                        ))}
                      </div>
                    )}

                    {threadMeta?.hasMore ? (
                      <button
                        type="button"
                        className={cls.loadMoreBtn}
                        disabled={threadLoading}
                        onClick={() =>
                          loadThread({
                            page: (threadMeta?.page || 1) + 1,
                            replace: false,
                          })
                        }
                      >
                        {t("post.actions.loadMore", "Load more")}
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <article
        className={`${cls.card} ${cls[`cardTheme--${postTheme}`] || ""}`}
        role="button"
        tabIndex={0}
        onClick={openModal}
      >
        <div className={cls.cardInner}>
          <div className={cls.header}>
            <AuthorWrap>
              <div className={cls.avatarGlow}>
                {primaryAvatarUrl ? (
                  <img
                    className={cls.avatar}
                    src={primaryAvatarUrl}
                    alt={primaryName}
                  />
                ) : (
                  <div className={cls.avatarFallback}>
                    {primaryName?.[0] || "M"}
                  </div>
                )}
              </div>

              <div className={cls.authorMeta}>
                {isClubPost ? (
                  <div className={cls.clubMetaStack}>
                    {clubHref ? (
                      <Link
                        to={clubHref}
                        className={cls.clubLink}
                        onClick={stop}
                      >
                        {clubName}
                      </Link>
                    ) : (
                      <span className={cls.clubName}>{clubName}</span>
                    )}
                    {hasText(authorName) ? (
                      authorHref ? (
                        <Link
                          to={authorHref}
                          className={cls.authorSubLink}
                          onClick={stop}
                        >
                          {authorName}
                        </Link>
                      ) : (
                        <span className={cls.authorSubName}>{authorName}</span>
                      )
                    ) : null}
                    {hasText(when) ? (
                      <div className={cls.timeLine}>{when}</div>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className={cls.authorName}>{authorName}</div>
                    <div className={cls.timeLine}>{when}</div>
                  </>
                )}
              </div>
            </AuthorWrap>

            <div className={cls.badges}>
              {isAuthor && viewHref ? (
                <Link to={viewHref} className={cls.viewBtn} onClick={stop}>
                  {t("post.actions.view", "View")}
                </Link>
              ) : (
                badges.map((b) => (
                  <span key={b} className={cls.badge}>
                    {b}
                  </span>
                ))
              )}
            </div>
          </div>

          {hasText(title) ? <h3 className={cls.title}>{title}</h3> : null}

          {hasText(desc) ? (
            <div className={cls.descWrap}>
              <p className={cls.descClamp2}>{desc}</p>
              {cardDescHasMore ? (
                <button
                  type="button"
                  className={cls.readMoreBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal();
                  }}
                >
                  {t("post.actions.readMore", "Read more")}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className={cls.mediaSlot} onClick={stop}>
            {renderVideo()}
          </div>

          <div className={cls.reactionsRow} onClick={stop}>
            {REACTIONS.map((r) => {
              const active = myReaction === r.key;
              const n = Number(counts?.[r.key] || 0);
              return (
                <button
                  key={r.key}
                  type="button"
                  className={`${cls.reactBtn} ${
                    active ? cls.reactBtnActive : ""
                  }`}
                  onClick={() => onReact(r.key)}
                  aria-pressed={active}
                >
                  <span className={cls.reactEmoji}>{r.emoji} </span>
                  <span className={cls.reactLabel}>{r.label} </span>
                  <span className={cls.reactCount}>{n}</span>
                </button>
              );
            })}

            <div className={cls.reactionMeta}>
              <span className={cls.metaPill}>
                ❤️ {Number(counts?.total || 0)}
              </span>
              <span className={cls.metaPill}>
                💬 {Number(post?.commentCount || 0)}
              </span>
            </div>
          </div>

          <div className={cls.commentsBox} onClick={stop}>
            <div className={cls.commentInputRow}>
              <textarea
                className={cls.commentInput}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t(
                  "post.inputs.commentPlaceholder",
                  "Write a comment…",
                )}
                rows={1}
                enterKeyHint="send"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitComment({ inModal: false });
                  }
                }}
              />
              <button
                type="button"
                className={cls.sendBtn}
                disabled={commentBusy || !hasText(commentText)}
                onClick={() => submitComment({ inModal: false })}
              >
                {t("post.actions.send", "Send")}
              </button>
            </div>

            {previewLoading ? (
              <div className={cls.smallHint}>
                {t("post.loading.loadingComments", "Loading comments…")}
              </div>
            ) : previewVisibleComments.length ? (
              <div className={cls.previewComments}>
                {previewVisibleComments.map((c) => (
                  <CommentItem
                    key={safeStr(c?._id)}
                    c={c}
                    inModal={false}
                    pickAuthor={pickCommentAuthor}
                    canDeleteComment={canDeleteComment}
                    canEditComment={canEditComment}
                    editId={editId}
                    editText={editText}
                    setEditText={setEditText}
                    editBusy={editBusy}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onSubmitEdit={submitEdit}
                    replyParentId={replyParentId}
                    replyText={replyText}
                    setReplyText={setReplyText}
                    replyBusy={replyBusy}
                    onStartReply={startReply}
                    onCancelReply={cancelReply}
                    onSubmitReply={submitReply}
                    confirmDelId={confirmDelId}
                    deleteBusy={deleteBusy}
                    onAskDelete={askDelete}
                    onConfirmDelete={confirmDelete}
                    onCancelDelete={cancelAskDelete}
                    onStop={stop}
                    t={t}
                  />
                ))}

                <button
                  type="button"
                  className={cls.viewAllBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal();
                  }}
                >
                  {t("post.actions.viewAllComments", "View all comments")}
                </button>
              </div>
            ) : (
              <div className={cls.smallHint}>
                {t("post.comments.first", "Be the first to comment.")}
              </div>
            )}
          </div>
        </div>
      </article>

      {modalUi}
    </>
  );
};

export default VideoPostCard;
